import type { MediaItem } from './components/MediaGrid'

import * as photoFiles from 'url:./assets/media/*.{jpg,jpeg,png,webp}'
import * as videoFiles from 'url:./assets/media/*.{mp4,webm,mov}'
import * as thumbFiles from 'url:./assets/media/thumbs/*.{jpg,jpeg,png,webp}'
import dimensions from './media-dimensions.json'

/**
 * Per-file caption, keyed by file name without its extension. `description` and
 * `credit` are i18n keys rather than literal text, so the banner follows the
 * selected language; `creditHref` optionally links the credit to its source.
 *
 * A file with no entry here still appears in the grid — it just carries no
 * caption and falls back to a generic label for screen readers.
 */
const CAPTIONS: Record<string, Omit<MediaItem, 'type' | 'src'>> = {
  '2026.08.04-fruit-seller-chased': {
    description: 'fruitSellerDescription',
    credit: 'khersonPoliceCredit',
    creditHref: 'https://t.me/khersonpolice',
  },
  '2026.08.20-human-safari-bus-attack': {
    description: 'busAttackDescription',
    credit: 'khersonPoliceCredit',
    creditHref: 'https://t.me/khersonpolice',
  },
  'Andrushenko, FPV drone': {
    description: 'fpvDroneDescription',
    credit: 'andrushenkoCredit',
  },
  'Andrushenko, human safari': {
    description: 'firefighterDescription',
    credit: 'andrushenkoCredit',
  },
}

/**
 * Parcel expands the globs above at build time into an object keyed by file
 * name, nested one level per capture: { basename: { extension: url } }. Those
 * keys are file names rather than identifiers, so the shape has to be cast.
 */
type GlobbedFiles = Record<string, Record<string, string>>

/** A leading `YYYY.MM.DD` in the file name is shown after the credit */
const DATE_PREFIX = /^(\d{4}\.\d{2}\.\d{2})/

/**
 * A file of the same name in `thumbs/` is used for the grid tile, so a tile
 * costs tens of kilobytes instead of pulling the full-size original. Without
 * one the tile falls back to the original.
 */
function thumbFor(name: string) {
  const byExtension = (thumbFiles as unknown as GlobbedFiles)[name]
  return byExtension && Object.values(byExtension)[0]
}

/**
 * Intrinsic size from `media-dimensions.json`, so the modal can reserve the
 * right shape before the file loads instead of resizing once it arrives.
 */
function aspectFor(name: string) {
  const [width, height] = (dimensions as Record<string, number[]>)[name] ?? []
  return width && height ? width / height : undefined
}

function collect(files: unknown, type: MediaItem['type']) {
  return Object.entries(files as GlobbedFiles).flatMap(([name, byExtension]) =>
    Object.values(byExtension).map((src) => ({
      name,
      item: {
        type,
        src,
        thumb: thumbFor(name),
        aspect: aspectFor(name),
        date: DATE_PREFIX.exec(name)?.[1],
        ...CAPTIONS[name],
      },
    })),
  )
}

/**
 * Everything in `src/assets/media/`, in file name order — drop a photo or video
 * in there and it appears. Only the first three reach the grid.
 */
export const MEDIA: MediaItem[] = [...collect(photoFiles, 'image'), ...collect(videoFiles, 'video')]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(({ item }) => item)
